import { useEffect, useState } from 'react';
import {
  getNotifications,
  getUnreadNotificationCount,
} from '../utils/notificationApi';

// Global notification cache to share between components
let notificationCache = {
  notifications: [],
  unreadCount: 0,
  lastFetch: null,
  isLoading: false,
};

// Subscribers for cache updates
let subscribers = [];

const notifySubscribers = () => {
  subscribers.forEach((callback) => callback(notificationCache));
};

const addSubscriber = (callback) => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter((sub) => sub !== callback);
  };
};

const updateCache = (updates) => {
  notificationCache = { ...notificationCache, ...updates };
  notifySubscribers();
};

const preloadNotifications = async () => {
  if (notificationCache.isLoading) return;

  try {
    updateCache({ isLoading: true });

    const [notifications, unreadCount] = await Promise.all([
      getNotifications(),
      getUnreadNotificationCount(),
    ]);

    updateCache({
      notifications: notifications || [],
      unreadCount: unreadCount || 0,
      lastFetch: Date.now(),
      isLoading: false,
    });
  } catch (error) {
    console.error('Error preloading notifications:', error);
    updateCache({ isLoading: false });
  }
};

// Hook for components that need to preload notifications
export const useNotificationPreloader = (user) => {
  useEffect(() => {
    if (user) {
      // Preload immediately when user is available
      preloadNotifications();

      // Set up background refresh every 30 seconds
      const interval = setInterval(preloadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  return {
    preloadNotifications,
    getCache: () => notificationCache,
  };
};

// Hook for components that need to consume cached notifications
export const useNotificationCache = () => {
  const [cache, setCache] = useState(notificationCache);

  useEffect(() => {
    const unsubscribe = addSubscriber(setCache);
    return unsubscribe;
  }, []);

  return cache;
};

// Function to get current cache synchronously
export const getNotificationCache = () => notificationCache;

// Function to manually refresh notifications
export const refreshNotifications = () => preloadNotifications();
